import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { inputCls } from "../admin/AFields";

const FIELDS = [
  { key: "fb_page_id", label: "Facebook Page ID", type: "text", hint: "Meta Business Suite → Page → About → Page ID" },
  { key: "fb_page_token", label: "Facebook Page Access Token", type: "password", hint: "Long-lived page token with pages_manage_posts permission" },
  { key: "fb_link", label: "Default link for Facebook posts (optional)", type: "text", hint: "e.g. https://greetings.sahajsarathi.com" },
];

function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${ok ? "bg-brandgreen-100 text-brandgreen" : "bg-red-50 text-red-500"}`} data-testid={`status-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {label}: {ok ? "CONFIGURED" : "NOT CONFIGURED"}
    </span>
  );
}

export default function StudioIntegrations() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/studio/settings").then((r) => { setData(r.data); setForm(r.data.integrations || {}); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/studio/settings", { integrations: form }); toast.success("Integrations saved"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="studio-integrations">
      <SEO title="Integrations" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Integrations</h1>
      <p className="mt-1 text-sm text-slate-500">Secrets are stored server-side and never shown again after saving (masked values are kept as-is).</p>

      {data && (
        <div className="mt-5 flex flex-wrap gap-2">
          <StatusPill ok={data.fb_configured} label="Facebook Page" />
          <StatusPill ok={data.ai_image_configured} label="AI (Emergent Key)" />
        </div>
      )}

      <div className="mt-8 max-w-3xl rounded-lg border border-gray-200 bg-white p-6">
        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{f.label}</label>
              <input type={f.type} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className={inputCls} autoComplete="new-password" data-testid={`ig-${f.key}`} />
              <p className="mt-1 text-xs text-slate-400">{f.hint}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end">
          <button onClick={save} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="integrations-save">
            {busy && <Loader2 size={14} className="animate-spin" />} Save Integrations
          </button>
        </div>
      </div>
    </div>
  );
}
